/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpStatusCode} from '@angular/common/http';
import {Component} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from "@angular/router";
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {StateService} from '../../../core/state/state.service';
import {ExcelExportService} from '../../../services/excel-export.service';
import {IndustryScenarioService} from '../../../services/industry-scenario.service';

@Component({
    selector: 'app-query-save-pop-up',
    templateUrl: './industry-query-save.component.html',
    styleUrl: './industry-query-save.component.scss',
    standalone: false
})
export class IndustryQuerySaveComponent {
  showSpinner: boolean = false;
  file: File;
  sheets: string[];
  sheetName: string;
  industryScenario: any;

  // Table Variables
  tableHeaders: ColDef[] = [
    {field: 'segment', headerName: 'Segment', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'tos', headerName: 'TOS', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'planningMarket', headerName: 'Planning Market', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'calYear', headerName: 'Calendar Year', filter: AutocompleteFilterComponent, lockPosition: true},
    {field: 'shares', headerName: 'Sum of Shares', filter: AutocompleteFilterComponent, lockPosition: true},
  ];
  invalidMixes: any[] = [];

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };


  constructor(public snackBar: FabMessageService,
              readonly industryScenarioService: IndustryScenarioService,
              public stateService: StateService,
              public dialog: MatDialog,
              public readonly excelService: ExcelExportService,
              readonly router: Router) {
  }

  /**
   * Validates the query save file
   */
  templateSaveValidate(): void {
    this.invalidMixes = [];
    this.showSpinner = true;
    this.industryScenarioService.queryIndustrySaveValidate(this.file, this.sheetName, this.stateService.getUserGmid()).subscribe(
      (data: any[]) => {
        if (data.length !== 0) {
          this.invalidMixes = data;
          this.invalidMixes.map(value => {
            value.shares = Math.round(value.shares * 100) + '%'
          })
          this.snackBar.displayError("Invalid mixes, please check table", DISMISS_MSG)
          this.showSpinner = false;
        } else {
          this.saveIndustryTemplate();
        }
      },
      (error) => {
        this.showSpinner = false;
        if (error.status === HttpStatusCode.MultipleChoices) {
          this.showTotalVehicleDialog(error.message);
        } else {
          this.industryScenario = null;
          this.snackBar.displayError(error, DISMISS_MSG);
        }
      }
    );
  }

  /**
   * Shows a dialog to confirm the query save
   */
  async showSaveConfirmationDialog(): Promise<void> {
    try {
      this.showSpinner = true;
      const industryScenarioPromise = this.industryScenarioService.getScenarioFromTemplate(this.file, this.sheetName).toPromise();
      this.industryScenario = await industryScenarioPromise;
      this.showSpinner = false;
      const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
        width: '400px',
        data: {
          title: 'Template Save Confirmation',
          message: 'Please ensure all appropriate segments are present on the Industry Template before saving.' +
            ' Do you wish to save data back to scenario ' + this.industryScenario.id + ': "' + this.industryScenario.industryScenarioName + '"?',
          noLabel: 'No',
          yesLabel: 'Yes',
          titleIcon: 'help',
          actionColor: 'primary'
        },
        disableClose: true
      });

      dialogRef.componentInstance.yes.subscribe(() => {
        this.templateSaveValidate();
      });
      dialogRef.componentInstance.no.subscribe(() => {
        this.industryScenario = null;
        dialogRef.close();
      });
    } catch {
      this.snackBar.displayError('Error fetching industryScenario.', DISMISS_MSG);
    }
  }

  /**
   * Shows a pop-up to confirm a total vehicle save when there are no volumes under certain years
   * @param message the message to show the users
   */
  showTotalVehicleDialog(message: string): void {
    const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Total Vehicle Missing Year Volumes',
        message,
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    });

    dialogRef.componentInstance.yes.subscribe(() => {
      this.saveIndustryTemplate()
    });
    dialogRef.componentInstance.no.subscribe(() => {
      this.industryScenario = null;
      dialogRef.close();
    });
  }

  /**
   * Runs the stored procedure to save the query
   */
  saveIndustryTemplate() {
    this.showSpinner = true;
    this.industryScenarioService.queryIndustrySave(this.file, this.sheetName, this.stateService.getUserGmid()).subscribe(
      {
        error: err => {
          this.snackBar.displayError(err, DISMISS_MSG);
          this.showSpinner = false;
        },
        complete: () => {
          this.showSpinner = false;
          this.showSuccessfulSaveDialog();
        }
      }
    );
  }

  /**
   * Shows a confirmation dialog after a successful save that allows the user to select the updated scenario
   */
  showSuccessfulSaveDialog() {
    const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Scenario Successfully Updated',
        message: 'Would you like to select scenario ' + this.industryScenario.id + ': "' + this.industryScenario.industryScenarioName + '"?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    });

    dialogRef.componentInstance.yes.subscribe(() => {
      this.stateService.setSelectedScenario(this.industryScenario, false);
      this.router.navigate(['/']);
    });
    dialogRef.componentInstance.no.subscribe(() => {
      dialogRef.close();
    });
  }

  /**
   * Uploads the selected file
   * @param event Map<string, any>
   */
  upload(event: any): void {
    this.sheets = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets,
      isMultiSelect: false
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.sheetName = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.file = null;
      },
      () => {
        if (this.sheetName.length === 0) {
          this.file = null;
          this.snackBar.displayError("Please re-upload file", DISMISS_MSG)
        } else {
          this.showSaveConfirmationDialog();
        }
      }
    );
  }
}
